import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '../../lib/utils.js';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | 'primary'
    | 'outline'
    | 'secondary'
    | 'ghost'
    | 'destructive'
    | 'destructiveSoft'
    | 'ai';
  size?: 'sm' | 'md' | 'lg';
}

const variants: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary:
    'bg-[var(--brand-500)] text-white border border-[var(--brand-500)] hover:bg-[var(--brand-600)] hover:border-[var(--brand-600)]',
  secondary:
    'bg-[var(--app-surface)] text-[var(--ink-700)] border border-[var(--app-divider-2)] hover:bg-[var(--app-hover)]',
  outline:
    'bg-transparent text-[var(--ink-700)] border border-[var(--app-divider-2)] hover:bg-[var(--app-hover)]',
  ghost:
    'bg-transparent text-[var(--ink-700)] border border-transparent hover:bg-[var(--app-hover)]',
  destructive: 'bg-[var(--danger)] text-white border border-[var(--danger)] hover:opacity-90',
  // Calm at rest (neutral outline + danger text), fills red only on hover — for
  // low-stakes destructive actions in lists, where a wall of solid-red buttons
  // would be visually overwhelming. Keep `destructive` for final confirms.
  destructiveSoft:
    'bg-transparent text-[var(--danger)] border border-[var(--app-divider-2)] hover:bg-[var(--danger)] hover:text-white hover:border-[var(--danger)]',
  ai: 'bg-[var(--ai-bg)] text-[var(--ai-fg)] border border-transparent hover:opacity-90',
};

const sizes: Record<NonNullable<ButtonProps['size']>, string> = {
  sm: 'h-7 px-3 text-body gap-1.5',
  md: 'h-9 px-3.5 text-body gap-2',
  lg: 'h-11 px-5 text-body gap-2',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', type = 'button', ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={cn(
        'inline-flex items-center justify-center font-medium font-sans whitespace-nowrap',
        'rounded-md [transition:transform_var(--dur-press)_var(--ease-out),background-color_var(--dur-hover)_var(--ease-out),border-color_var(--dur-hover)_var(--ease-out),opacity_var(--dur-hover)_var(--ease-out)]',
        'active:scale-[0.97]',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-500)]/40 focus-visible:ring-offset-1 focus-visible:ring-offset-[var(--app-bg)]',
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100',
        variants[variant],
        sizes[size],
        className,
      )}
      {...props}
    />
  ),
);
Button.displayName = 'Button';
